
import {defineComponent} from "vue";
import TranscriptionsComponent from "@/components/TranscriptionsComponent.vue";
import NetworkImageComponent from "@/components/NetworkImageComponent.vue";
import {PlayerState, PlayerStoreInterface, usePlayerStore} from "@/store/player.store";
import {mapActions, mapState, Store} from "pinia";
import {useAnalyticsStore} from "@/store/analytics.store";
import {FastAverageColor, FastAverageColorOptions, FastAverageColorResult} from "fast-average-color";
import {optimiseBackgroundColor} from "@/utils/dominantColorOptimiser";
import {useUIStore} from "@/store/ui.store";
import {Image} from "@/models/image";
import playerjs from 'player.js'
import {Void} from "@/graphql/API";

interface MouseScrubData {
    minValue: number;
    maxValue: number;
    isPaused: boolean;
}

interface PostPlayerComponentData {
    firstTimePlay: boolean;
    isScrubbing: boolean;
    changePostAnimation: boolean;
    mouseScrubData?: MouseScrubData;
    playerStore?: Store<'player', PlayerStoreInterface>;
    // eslint-disable-next-line
    playerJSReceiver?: any;
}

export default defineComponent({
    name: "BasePlayerComponent",
    components: { TranscriptionsComponent, NetworkImageComponent },
    props: {
        showShareOverlay: { type: Function, required: false },
        appearance: { type: String, required: false },
        showLink: { type: Boolean, required: false },
    },
    data(): PostPlayerComponentData {
        return {
            firstTimePlay: true,
            isScrubbing: false,
            changePostAnimation: false,
        };
    },
    mounted(): void {
        this.playerStore = usePlayerStore();

        this.playerJSReceiver = new playerjs.Receiver();
        this.preparePlayer(this.post?.audio?.url);

        this.playerStore.$subscribe((_, { state, playback }) => {
            if (state === PlayerState.ended) {
                this.firstTimePlay = true;
                this.playerJSReceiver.emit('ended')
            } else if (state === PlayerState.playing) {
                this.firstTimePlay = false;
            }

            if (playback) {
              if (playback.duration) {
                this.playerJSReceiver.ready();
              }

              this.playerJSReceiver.emit('timeupdate', {
                seconds: playback.currentTime,
                duration: playback.duration,
              });
            }
        });

        this.calculateDominantColor();

      this.playerJSReceiver.on('play', () => {
        this.play();
        this.playerJSReceiver.emit('play');
      });

      this.playerJSReceiver.on('pause', () => {
        this.pause();
        this.playerJSReceiver.emit('pause');
      });

      this.playerJSReceiver.on('getDuration', (callback: ((_: number)=>Void)) => callback(this.duration));
      this.playerJSReceiver.on('getCurrentTime', (callback: ((_: number)=>Void)) => callback(this.originalCurrentTime));
      this.playerJSReceiver.on('setCurrentTime', (time: number) => this.setCurrentTime(time));
      this.playerJSReceiver.on('getVolume', (callback: ((_: number)=>Void)) => callback(this.volume*100));
      this.playerJSReceiver.on('setVolume', (value: number) => this.setVolume(value/100));
      this.playerJSReceiver.on('mute', () => this.setMuted(true))
      this.playerJSReceiver.on('unmute', () => this.setMuted(false));
      this.playerJSReceiver.on('getMuted', (callback: ((_: boolean)=>Void)) => callback(this.isMuted));
      this.playerJSReceiver.on('getLoop', (callback: ((_: boolean)=>Void)) => callback(this.loop));
      this.playerJSReceiver.on('setLoop', (loop: boolean) => this.setLoop(loop));
      this.playerJSReceiver.on('getPaused', (callback: ((_: boolean)=>Void)) => callback(!this.isPlaying));
    },
    watch: {
        async post() {
            await this.calculateDominantColor();

            // if there is post and post doesn't have audio url, redirect to home
            if (this.post && !this.post?.audio?.url) {
                this.$router.push({ name: 'home' });
                return;
            }
        },
    },
    methods: {
        // player store actions
        ...mapActions(usePlayerStore, ['preparePlayer']),
        ...mapActions(usePlayerStore, ['seek']),
        ...mapActions(usePlayerStore, ['rawPlay']),
        ...mapActions(usePlayerStore, ['rawPause']),
        ...mapActions(usePlayerStore, ['play']),
        ...mapActions(usePlayerStore, ['pause']),
        ...mapActions(usePlayerStore, ['resetPlayer']),
        ...mapActions(usePlayerStore, ['setActivePost']),
        ...mapActions(usePlayerStore, ['setVolume']),
        ...mapActions(usePlayerStore, ['setLoop']),
        ...mapActions(usePlayerStore, ['setCurrentTime']),
        ...mapActions(usePlayerStore, ['setMuted']),

        // analytics
        ...mapActions(useAnalyticsStore, ["trackClickOut"]),

        // seek
        onMouseDown(event: PointerEvent): void {
            const eventTarget: EventTarget | null = event.target;
            if (!eventTarget) {
                return;
            }

            this.isScrubbing = true;

            // eslint-disable-next-line
            const elementWidth = (this.$refs["progressBar"] as any).clientWidth;
            const clickedXOffset = event.offsetX;

            // click on progress point is disabled
            // eslint-disable-next-line
            if ((eventTarget as any).id !== "progress-point") {
                this.seek(clickedXOffset / elementWidth);
            }

            // sum of progress container and progressbar offset
            // eslint-disable-next-line
            const minValue = (this.$refs["progressContainer"] as any).offsetLeft + (this.$refs["progressBar"] as any).offsetLeft;
            const maxValue = minValue + elementWidth;
            this.mouseScrubData = {
                minValue: minValue,
                maxValue: maxValue,
                isPaused: !this.isPlaying,
            };

            this.rawPause();

            document.addEventListener("mousemove", this.onMouseMove);
            document.addEventListener("mouseup", this.onMouseUp);
        },
        onMouseMove(event: MouseEvent): void {
            if (!this.mouseScrubData) {
                return;
            }

            let offset = 0;
            if (event.clientX < this.mouseScrubData.minValue) {
                offset = 0;
            } else if (event.clientX > this.mouseScrubData.maxValue) {
                offset = 1;
            } else {
                const width = this.mouseScrubData.maxValue - this.mouseScrubData.minValue;
                const position = event.clientX - this.mouseScrubData.minValue;
                offset = position / width;
            }

            this.seek(offset);
        },
        onMouseUp(): void {
            document.removeEventListener("mousemove", this.onMouseMove);
            document.removeEventListener("mouseup", this.onMouseUp);
            this.isScrubbing = false;
            if (!this.mouseScrubData?.isPaused) {
                this.rawPlay();
            }
        },
        slideChangeAnimation() {
            this.changePostAnimation = true;
            const interval = setInterval(() => {
                this.changePostAnimation = false;
                clearInterval(interval);
            }, 300);
        },
        togglePlayPause(): void {
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        },
        async calculateDominantColor(): Promise<void> {
            if (this.appearance !== 'dominant') {
                return;
            }

            if (!this.getPostImage) {
                return;
            }

            try {
                const fac = new FastAverageColor();
                const imageURL: string = this.getPostImage.urlTemplate.replace("---WIDTH---", `60`).replace("---HEIGHT---", `90`);
                const options: FastAverageColorOptions = {
                    algorithm: "sqrt",
                    mode: "precision",
                };
                const result: FastAverageColorResult = await fac.getColorAsync(imageURL, options);
                fac.destroy();

                const c = optimiseBackgroundColor("#ffffff", result.hex);

                const root = document.documentElement;
                root.style.setProperty('--background', c);
                root.style.setProperty('--primary', "255 255 255");
            } catch (e) {
                console.log(e);
            }
        }
    },
    computed: {
        // state properties player
        ...mapState(usePlayerStore, {post: 'getActivePost'}),
        ...mapState(usePlayerStore, ['isPlaying']),
        ...mapState(usePlayerStore, ['isPaused']),
        ...mapState(usePlayerStore, ['originalCurrentTime']),
        ...mapState(usePlayerStore, ['progress']),
        ...mapState(usePlayerStore, ['currentTime']),
        ...mapState(usePlayerStore, ['duration']),
        ...mapState(usePlayerStore, ['playback']),
        ...mapState(usePlayerStore, ['getQueue']),
        ...mapState(usePlayerStore, ['volume']),
        ...mapState(usePlayerStore, ['loop']),
        ...mapState(usePlayerStore, ['isMuted']),
        ...mapState(usePlayerStore, ['getState']),
        // analytics
        ...mapState(useAnalyticsStore, ["portalShareURL"]),
        ...mapState(useAnalyticsStore, ["postShareURL"]),
        // ui
        ...mapState(useUIStore, ['getHideLogo']),

        getPostImage(): Image | undefined {
            if (this.post?.image) {
                return this.post?.image;
            } else if (this.post?.profile.image) {
                return this.post.profile.image;
            }

            return undefined;
        },
        getCaptionOrTranscripts(): string | null {
            if (this.post === null) {
                return null;
            }
            if (this.post?.caption) {
                return this.getCaption;
            } else if (this.post?.rawTranscript) {
                return this.post?.rawTranscript;
            } else {
                return null;
            }
        },
        getCaption(): string | null {
            if (this.post === null) {
                return null;
            }
            if (this.post?.caption && this.showLink) {
                return this.post?.caption;
            } else if (this.post?.caption && !this.showLink) {
                // super duper mega high-end link-detection regex from https://daringfireball.net/2010/07/improved_regex_for_matching_urls
                const linkRegex = /\b((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|www\d{0,3}[.]|[a-z0-9.-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()[\]{};:'".,<>?«»“”‘’]))/g;
                return this.post?.caption.replace(linkRegex, '')
            } else {
                return null;
            }
        },
        getPostLinkHost(): string | null {
            if (this.post === undefined || !this.post.link) {
                return null
            }

            const url: URL = new URL(this.post.link?.url);
            return url.host;
        },
        truncatedRawTranscription(): string {
            if (this.post && this.post.rawTranscript && this.post.rawTranscript.length < 56) {
                return this.post.rawTranscript;
            }

            return this.post?.rawTranscript ? `${this.post.rawTranscript.substring(0, 56).trim()}...` : "";
        },
        showTranscriptionsComponent(): boolean {
            return this.isPlaying || this.isPaused;
        },
        showRawTranscriptions(): boolean {
            return !this.showTranscriptionsComponent;
        },
        contributorName(): string {
            if (this.post?.contributorName) {
                return this.post?.contributorName;
            } else if (this.post?.profile.name) {
                return this.post.profile.name;
            } else if (this.post?.profile.url) {
                return this.post.profile.url;
            }

            return "";
        },
        mainDomainUrl(): string {
            return `https://${process.env.VUE_APP_DOMAIN_NAME}`
        }
    },
});
